import { getAiApiClient } from "./aiClient";
import {
  AiAsset,
  AiJob,
  AiPromptPreset,
  ApiResponse,
  GenerateTextInput,
  GenerateTextOutput,
  ImageActionInput,
  ListingEntityType,
} from "./components/types/ai.types";

const unwrap = <T>(response: { data: ApiResponse<T> }): T => {
  if (!response.data.success) {
    throw new Error(
      response.data.message || response.data.error || "Request failed",
    );
  }

  return response.data.data as T;
};

export const getAiPresets = async (): Promise<AiPromptPreset[]> => {
  const response =
    await getAiApiClient().get<ApiResponse<AiPromptPreset[]>>("/api/v1/ai/presets");

  return unwrap(response) || [];
};

export const getPresets = async (
  entityType?: ListingEntityType,
): Promise<AiPromptPreset[]> => {
  const rows = await getAiPresets();

  if (!entityType) return rows;

  return rows.filter((preset) => {
    return (
      !preset.entityTypes?.length || preset.entityTypes.includes(entityType)
    );
  });
};

export const seedAiPresets = async (): Promise<AiPromptPreset[]> => {
  const response = await getAiApiClient().post<ApiResponse<AiPromptPreset[]>>(
    "/api/v1/ai/presets/seed",
  );

  return unwrap(response) || [];
};

export const generateAiText = async (
  input: GenerateTextInput,
): Promise<GenerateTextOutput> => {
  const response = await getAiApiClient().post<ApiResponse<GenerateTextOutput>>(
    "/api/v1/ai/text/generate",
    input,
  );

  return unwrap(response);
};

/**
 * Legacy wrapper because useAiCore.ts expects:
 * const res = await aiApi.generateText(payload);
 * setData(res.data);
 */
export const generateText = async (
  input: GenerateTextInput,
): Promise<ApiResponse<GenerateTextOutput>> => {
  const data = await generateAiText(input);

  return {
    success: true,
    data,
  };
};

export const uploadMedia = async ({
  entityType,
  entityId,
  file,
  kind = "original",
}: {
  entityType: ListingEntityType;
  entityId: string;
  file: File;
  kind?: string;
}): Promise<AiAsset> => {
  const formData = new FormData();

  formData.append("file", file);
  formData.append("entityType", entityType);
  formData.append("entityId", entityId);
  formData.append("kind", kind);

  const response = await getAiApiClient().post<ApiResponse<AiAsset>>(
    "/api/v1/media/upload",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );

  const asset = unwrap(response);

  return {
    ...asset,
    originalName:
      asset.originalName ||
      asset.metadata?.originalName ||
      asset.metadata?.filename,
    filename: asset.filename || asset.metadata?.filename,
    size: asset.size || asset.metadata?.size,
  };
};

export const getAiAssetsForEntity = async (
  entityType: ListingEntityType,
  entityId: string,
): Promise<AiAsset[]> => {
  const response = await getAiApiClient().get<ApiResponse<AiAsset[]>>(
    `/api/v1/ai/assets/entity/${entityType}/${entityId}`,
  );

  return unwrap(response) || [];
};

export const getAssetsForEntity = getAiAssetsForEntity;

export const getMediaAssetsForEntity = async (
  entityType: ListingEntityType,
  entityId: string,
): Promise<AiAsset[]> => {
  const response = await getAiApiClient().get<ApiResponse<AiAsset[]>>(
    `/api/v1/media/entity/${entityType}/${entityId}`,
  );

  const rows = unwrap(response) || [];

  return rows.map((asset) => ({
    ...asset,
    originalName:
      asset.originalName ||
      asset.metadata?.originalName ||
      asset.metadata?.filename,
    filename: asset.filename || asset.metadata?.filename,
    size: asset.size || asset.metadata?.size,
  }));
};

export const getJobsForEntity = async (
  entityType: ListingEntityType,
  entityId: string,
): Promise<AiJob[]> => {
  const response = await getAiApiClient().get<ApiResponse<AiJob[]>>(
    `/api/v1/ai/jobs/entity/${entityType}/${entityId}`,
  );

  return unwrap(response) || [];
};

export const removeBackground = async (
  input: ImageActionInput,
): Promise<AiAsset> => {
  const response = await getAiApiClient().post<ApiResponse<AiAsset>>(
    "/api/v1/ai/images/remove-background",
    input,
  );

  return unwrap(response);
};

export const enhanceImage = async (
  input: ImageActionInput,
): Promise<AiAsset> => {
  const response = await getAiApiClient().post<ApiResponse<AiAsset>>(
    "/api/v1/ai/images/enhance",
    input,
  );

  return unwrap(response);
};

export const generatePoster = async (
  input: ImageActionInput,
): Promise<AiAsset> => {
  const response = await getAiApiClient().post<ApiResponse<AiAsset>>(
    "/api/v1/ai/images/generate-poster",
    input,
  );

  return unwrap(response);
};

export const aiApi = {
  getAiPresets,
  getPresets,
  seedAiPresets,

  generateAiText,
  generateText,

  uploadMedia,

  getAiAssetsForEntity,
  getAssetsForEntity,
  getMediaAssetsForEntity,
  getJobsForEntity,

  removeBackground,
  enhanceImage,
  generatePoster,
};
